/**
 * Security Service API Client
 * Communicates with the saf3ai-security-service microservice
 */
import { emitSessionExpired } from '../auth/sessionExpiry';
import { withSurfaceQuery } from './surfaceScope';

// Support multiple sources for Security API URL
let SECURITY_API_BASE = 'http://localhost:8095/api/v1';

// Priority 1: Runtime config (env-config.js)
if (typeof window !== 'undefined' && window.env && window.env.SECURITY_SERVICE_URL) {
  SECURITY_API_BASE = window.env.SECURITY_SERVICE_URL;
}
// Priority 2: Build-time environment variable
else if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SECURITY_API_URL) {
  SECURITY_API_BASE = process.env.REACT_APP_SECURITY_API_URL;
}

// Normalize the URL
if (SECURITY_API_BASE) {
  SECURITY_API_BASE = SECURITY_API_BASE.trim().replace(/\/+$/, '');

  // Ensure URL is absolute for fetch calls
  if (!SECURITY_API_BASE.match(/^https?:\/\//) && typeof window !== 'undefined' && window.location) {
    const origin = window.location.origin;
    SECURITY_API_BASE = SECURITY_API_BASE.startsWith('/')
      ? `${origin}${SECURITY_API_BASE}`
      : `${origin}/${SECURITY_API_BASE}`;
  }

  // If page is HTTPS, ensure API URL is also HTTPS (fix mixed content issues)
  if (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:') {
    if (SECURITY_API_BASE.startsWith('http://')) {
      SECURITY_API_BASE = SECURITY_API_BASE.replace(/^http:\/\//, 'https://');
    }
  }
}

console.log('Security API Base URL:', SECURITY_API_BASE);

const buildQs = (query) => (query ? `?${query}` : '');

// Helper function to ensure HTTPS when page is HTTPS (fixes mixed content issues)
const ensureHttps = (url) => {
  if (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:') {
    if (url && url.startsWith('http://')) {
      return url.replace(/^http:\/\//, 'https://');
    }
  }
  return url;
};

// Make security API requests using fetch
const makeSecurityRequest = async (endpoint, options = {}) => {
  try {
    const token = localStorage.getItem('jwt_token');
    let orgId = null;
    try {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        const user = JSON.parse(rawUser);
        orgId = user?.organization_id || user?.org_id || user?.organizationId || null;
      }
    } catch {
      // ignore malformed user session payload
    }
    // Ensure HTTPS at request time as well (safety net)
    const baseUrl = ensureHttps(SECURITY_API_BASE);
    // Inside a pinned page (<SurfaceScope>) every call names its surface, so the
    // service can check the module licence for it (surfaceScope.withSurfaceQuery).
    let resolvedEndpoint = withSurfaceQuery(endpoint || '');
    // Enforce organization scoping for security APIs that support it.
    if (orgId) {
      const hasQuery = resolvedEndpoint.includes('?');
      const hasOrg = /(?:\?|&)organization_id=/.test(resolvedEndpoint);
      if (!hasOrg) {
        resolvedEndpoint = `${resolvedEndpoint}${hasQuery ? '&' : '?'}organization_id=${encodeURIComponent(String(orgId))}`;
      }
    }
    const fullUrl = ensureHttps(`${baseUrl}${resolvedEndpoint}`);
    
    const response = await fetch(fullUrl, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });

    if (response.status === 401) {
      const isLoginRequest = String(endpoint || '').includes('/login');
      if (!isLoginRequest) {
        emitSessionExpired('Your session has expired. Continue your current session or sign in again.');
      }
      const unauthorizedError = new Error('Unauthorized');
      unauthorizedError.status = 401;
      throw unauthorizedError;
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Security API error: ${endpoint}`, error);
    throw error;
  }
};

export const securityService = {
  // ============ OWASP Threat Control ============
  owasp: {
    getDashboardStats: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/owasp/dashboard-stats${buildQs(query)}`);
    },
    getVulnerabilities: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/owasp/vulnerabilities${buildQs(query)}`);
    },
    getAgentVulnerabilities: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/owasp/agents${buildQs(query)}`);
    },
    getAtlasMatrix: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/owasp/atlas-matrix${buildQs(query)}`);
    },
    getTrend: (days = 7, params = {}) => {
      const queryParams = new URLSearchParams();
      queryParams.append('days', String(days));
      if (params.time_range) queryParams.append('time_range', params.time_range);
      if (params.agent_id) queryParams.append('agent_id', String(params.agent_id));
      const query = queryParams.toString();
      return makeSecurityRequest(`/owasp/trend${buildQs(query)}`);
    },
    getPostureData: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/owasp/posture-data${buildQs(query)}`);
    },
  },

  // ============ Data Security ============
  dataSecurity: {
    getStats: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/data-security/stats${buildQs(query)}`);
    },
    getPiiBreakdown: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/data-security/pii-breakdown${buildQs(query)}`);
    },
    getDetections: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/data-security/detections${buildQs(query)}`);
    },
    getAgentStats: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/data-security/agents${buildQs(query)}`);
    },
    getLineage: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/data-security/lineage${buildQs(query)}`);
    },
  },

  // ============ Threat Intelligence ============
  threatIntelligence: {
    getStats: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/threat-intelligence/stats${buildQs(query)}`);
    },
    getRecentThreats: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/threat-intelligence/recent${buildQs(query)}`);
    },
    getAttackPatterns: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/threat-intelligence/attack-patterns${buildQs(query)}`);
    },
    getToolAbuse: (limit = 20, params = {}) => {
      const query = new URLSearchParams({ limit, ...params }).toString();
      return makeSecurityRequest(`/threat-intelligence/tool-abuse${buildQs(query)}`);
    },
    getToolAbuseChart: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/threat-intelligence/tool-abuse-chart${buildQs(query)}`);
    },
    getAnomalies: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/threat-intelligence/anomalies${buildQs(query)}`);
    },
    getIocData: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/threat-intelligence/ioc${buildQs(query)}`);
    },
    getAttackChains: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/threat-intelligence/attack-chains${buildQs(query)}`);
    },
    getGeoDistribution: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/threat-intelligence/geo-distribution${buildQs(query)}`);
    },
  },


  // ============ Attack Path ============
  attackPath: {
    getStats: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/attack-path/stats${buildQs(query)}`);
    },
    getDetectedPaths: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/attack-path/detected${buildQs(query)}`);
    },
    getSimulatedPaths: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/attack-path/simulated${buildQs(query)}`);
    },
    getIncidents: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/attack-path/incidents${buildQs(query)}`);
    },
    getIncidentDetail: (conversationId) =>
      makeSecurityRequest(`/attack-path/incidents/${encodeURIComponent(conversationId)}`),
    getSpanTopology: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/attack-path/topology${buildQs(query)}`);
    },
    getReviewQueue: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/attack-path/review/queue${buildQs(query)}`);
    },
    submitReviewLabel: (spanId, body) =>
      makeSecurityRequest(`/attack-path/review/${encodeURIComponent(spanId)}/label`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    getReviewStats: () => makeSecurityRequest('/attack-path/review/stats'),
    runSimulation: (data) => makeSecurityRequest('/attack-path/simulate', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  },

  // ============ Incident Response ============
  incidents: {
    getStats: () => makeSecurityRequest('/incidents/stats'),
    getIncidents: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/incidents${buildQs(query)}`);
    },
    getIncident: (id) => makeSecurityRequest(`/incidents/${id}`),
    getPlaybooks: () => makeSecurityRequest('/incidents/playbooks'),
    createIncident: (data) => makeSecurityRequest('/incidents', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  },

  // ============ Security Posture ============
  securityPosture: {
    getScore: () => makeSecurityRequest('/security-posture/score'),
    getCategoryScores: () => makeSecurityRequest('/security-posture/categories'),
    getControls: () => makeSecurityRequest('/security-posture/controls'),
    getRisks: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/security-posture/risks${buildQs(query)}`);
    },
    getFullBreakdown: () => makeSecurityRequest('/security-posture/breakdown'),
  },

  // ============ Audit Log ============
  audit: {
    getStats: (days = 7) => makeSecurityRequest(`/audit/stats?days=${days}`),
    getLogs: (params = {}) => {
      // Build query string, handling enum values properly
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      if (params.event_type) queryParams.append('event_type', params.event_type);
      if (params.severity) queryParams.append('severity', params.severity);
      if (params.actor) queryParams.append('actor', params.actor);
      if (params.agent) queryParams.append('agent', params.agent);
      // Date range filtering
      if (params.days !== undefined && params.days !== null) queryParams.append('days', params.days);
      if (params.time_range) queryParams.append('time_range', params.time_range);
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      const queryString = queryParams.toString();
      return makeSecurityRequest(`/audit${queryString ? `?${queryString}` : ''}`);
    },
    exportLogs: (format = 'json', days = 30) =>
      makeSecurityRequest(`/audit/export?format=${format}&days=${days}`),
    searchLogs: (query, limit = 50) =>
      makeSecurityRequest(`/audit/search?q=${encodeURIComponent(query)}&limit=${limit}`),
    // Optimized component-level endpoints
    getAccessPatterns: (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.time_range) queryParams.append('time_range', params.time_range);
      if (params.days !== undefined && params.days !== null) queryParams.append('days', params.days);
      if (params.agent) queryParams.append('agent', params.agent);
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      const qs = queryParams.toString();
      return makeSecurityRequest(`/audit/charts/access-patterns${qs ? `?${qs}` : ''}`);
    },
    getRiskDistribution: (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.time_range) queryParams.append('time_range', params.time_range);
      if (params.days !== undefined && params.days !== null) queryParams.append('days', params.days);
      if (params.agent) queryParams.append('agent', params.agent);
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      const qs = queryParams.toString();
      return makeSecurityRequest(`/audit/charts/risk-distribution${qs ? `?${qs}` : ''}`);
    },
    getTopUsers: (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.time_range) queryParams.append('time_range', params.time_range);
      if (params.days !== undefined && params.days !== null) queryParams.append('days', params.days);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.agent) queryParams.append('agent', params.agent);
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      const qs = queryParams.toString();
      return makeSecurityRequest(`/audit/charts/top-users${qs ? `?${qs}` : ''}`);
    },
    getFilterOptions: (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.time_range) queryParams.append('time_range', params.time_range);
      if (params.days !== undefined && params.days !== null) queryParams.append('days', params.days);
      const qs = queryParams.toString();
      return makeSecurityRequest(`/audit/filter-options${qs ? `?${qs}` : ''}`);
    },
    getLogsPaginated: (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset !== undefined) queryParams.append('offset', params.offset);
      if (params.include_total !== undefined) queryParams.append('include_total', params.include_total);
      if (params.lightweight !== undefined) queryParams.append('lightweight', params.lightweight);
      if (params.event_type) queryParams.append('event_type', params.event_type);
      if (params.severity) queryParams.append('severity', params.severity);
      if (params.actor) queryParams.append('actor', params.actor);
      if (params.agent) queryParams.append('agent', params.agent);
      if (params.resource) queryParams.append('resource', params.resource);
      if (params.status) queryParams.append('status', params.status);
      if (params.time_range) queryParams.append('time_range', params.time_range);
      if (params.days !== undefined && params.days !== null) queryParams.append('days', params.days);
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      const qs = queryParams.toString();
      return makeSecurityRequest(`/audit/logs${qs ? `?${qs}` : ''}`);
    },
  },

  // ============ Compliance Evidence ============
  compliance: {
    getStats: () => makeSecurityRequest('/compliance-evidence/stats'),
    getFrameworks: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/compliance-evidence/frameworks${buildQs(query)}`);
    },
    getFramework: (frameworkId) => makeSecurityRequest(`/compliance-evidence/frameworks/${frameworkId}`),
    getFrameworkControls: (frameworkId, params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/compliance-evidence/frameworks/${frameworkId}/controls${buildQs(query)}`);
    },
    getControl: (controlId) => makeSecurityRequest(`/compliance-evidence/controls/${controlId}`),
    getControlLogs: (controlId, params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/compliance-evidence/controls/${controlId}/logs${buildQs(query)}`);
    },
    getSummary: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/compliance-evidence/summary${buildQs(query)}`);
    },
    getEvidence: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return makeSecurityRequest(`/compliance-evidence/evidence${buildQs(query)}`);
    },
    uploadEvidence: (data) => makeSecurityRequest('/compliance-evidence/evidence', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    exportReport: (frameworkId, format = 'csv', params = {}) => {
      const query = new URLSearchParams({ format, ...params }).toString();
      return makeSecurityRequest(`/compliance-evidence/export/${frameworkId}?${query}`);
    },
    // Download report as file
    downloadReport: async (frameworkId, format = 'csv', params = {}) => {
      const query = new URLSearchParams({ format, ...params }).toString();
      const baseUrl = ensureHttps(SECURITY_API_BASE);
      const url = ensureHttps(`${baseUrl}${withSurfaceQuery(`/compliance-evidence/export/${frameworkId}?${query}`)}`);
      const token = localStorage.getItem('jwt_token');
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      if (response.status === 401) {
        emitSessionExpired('Your session has expired. Continue your current session or sign in again.');
        throw new Error('Unauthorized');
      }
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      if (format === 'csv') {
        const blob = await response.blob();
        return blob;
      }
      return response.json();
    },
  },

  // ============ AI Investigation (via AI Microservice) ============
  ai: {
    getInvestigation: async (logId, source = 'sdk_telemetry', forceRegenerate = false, sourceIndex = '') => {
      const { aiClient } = await import('../api/aiClient');
      const params = {
        source: source,
        ...(sourceIndex && { source_index: sourceIndex }),
        ...(forceRegenerate && { force_regenerate: 'true' })
      };
      const response = await aiClient.get(`/agent/get-log-investigation/${logId}`, { params });
      return response.data;
    },
  },
};

export default securityService;
